// SNOWCLUB-LAUNCH-REMAINING-PARITY-001 — the extraction's TEXT blocks, served with their own structure.
//
// WHAT THIS REPLACES. The crawlable layer rendered every text block as a bare <p>: a captured <h2> and
// the FAQ question under it were delivered as paragraphs, so the raw document carried live's words and
// none of live's heading outline (measured on the exact candidate: the no-JS layer of every Gold page
// exposed a single heading, the shell's <h1>, against 5-10 on the legacy page). Same blocks, same order,
// same text — now a heading is a heading and a list item is a list item.
//
// ONE <h1> STAYS ONE. ExportShell owns it; a captured level-1 heading is served as an <h2> (see
// `headingTag`). Blocks with no text (images, tables) are not this component's business and render
// nothing here, exactly as before — the image blocks are rendered by the caller.
//
// `tabs` — LIVE'S OWN SECTION TABS, MADE CRAWLABLE. Every transport-information capture opens with the two
// labels live renders as tab buttons ("General Information", "Airport Information"). Live wires them with
// JavaScript, so the general-information URL has no crawlable inbound link anywhere on live — it ranks
// (Banff: 10,231 winter impressions) with nothing pointing at it. Here the same two captured labels are
// served as ordinary anchors to this mountain's own two pages; the page a visitor is ON is served as
// text with aria-current, never as a link to itself. No label is invented and no other block changes.
//
// `paint` marks the blocks `@/lib/painted-content` judged fit for the visitor's surface; the client
// component ServerContentPaint clones exactly those. Unmarked blocks are still served, as before.
import { headingTag, paintedContentIndexes } from "@/lib/painted-content";
import type { PaintBlock } from "@/lib/painted-content";

export default function ServerContentBlocks({
  blocks,
  paint,
  tabs,
  currentPath,
}: {
  blocks: readonly PaintBlock[];
  paint: boolean;
  /** captured tab label -> this mountain's own URL for it */
  tabs?: Readonly<Record<string, string>>;
  currentPath?: string;
}) {
  const marked = new Set(paint ? paintedContentIndexes(blocks).keep : []);
  const firstHeading = blocks.findIndex((b) => b?.type === "heading");
  const mark = (i: number) => (marked.has(i) ? { "data-sg-paint": "" } : {});
  const out: React.ReactNode[] = [];
  let run: React.ReactNode[] = [];
  const flush = (key: string) => {
    if (run.length) out.push(<ul key={`u${key}`}>{run}</ul>);
    run = [];
  };
  blocks.forEach((b, i) => {
    const text = b?.text;
    if (!text) return;
    if (b.type === "listitem") {
      run.push(<li key={`b${i}`} {...mark(i)}>{text}</li>);
      return;
    }
    flush(String(i));
    if (b.type === "heading") {
      const Tag = headingTag(b.level);
      out.push(<Tag key={`b${i}`} {...mark(i)}>{text}</Tag>);
      return;
    }
    // A captured tab label, ahead of the copy: an anchor to its page (or the current page, named).
    const tabHref = tabs && b.type === "paragraph" && (firstHeading < 0 || i < firstHeading) ? tabs[text.trim()] : undefined;
    if (tabHref) {
      out.push(
        <p key={`b${i}`} data-sg-tab="" {...(paint ? { "data-sg-paint": "" } : {})}>
          {tabHref === currentPath ? <span aria-current="page">{text}</span> : <a href={tabHref}>{text}</a>}
        </p>,
      );
      return;
    }
    out.push(<p key={`b${i}`} {...mark(i)}>{text}</p>);
  });
  flush("end");
  return <>{out}</>;
}
